/**
 * BulkImportWizard - Main component for bulk data import functionality
 * 
 * IMPORTANT SESSION CLARIFICATION:
 * This system uses TWO different types of sessions:
 * 
 * 1. USER AUTHENTICATION SESSION (from sessionStorage):
 *    - sId: sessionStorage.getItem("sessionId") - Encrypted user session
 *    - uId: sessionStorage.getItem("uId") - User ID
 *    - These are passed to ALL API calls for authentication
 *    - NO new authentication sessions are created by this module
 * 
 * 2. IMPORT SESSION (database record in import_sessions table):
 *    - Created when a file is uploaded
 *    - Tracks the progress of a specific import operation
 *    - Has its own ID (import_sessions_Id) returned as 'session_id' from upload API
 *    - This is NOT a new user session, just a tracking record
 */
import React, { Component } from "react";
import { Button, Container, Row, Col, Card } from "react-bootstrap";
import { toast } from "react-toastify";
import configData from "../../config.json";

// Import step components
import FileUploadStep from "./steps/FileUploadStep";
import TableSelectionStep from "./steps/TableSelectionStep";
import FieldMappingStep from "./steps/FieldMappingStep";
import ValidationStep from "./steps/ValidationStep";
import ProcessingStep from "./steps/ProcessingStep";
import ResultsStep from "./steps/ResultsStep";

export default class BulkImportWizard extends Component {
  constructor(props) {
    super(props);
    
    this.state = {
      currentStep: props.session ? this.getInitialStep(props.session) : 0,
      
      // Import session data
      importSession: {
        sessionId: props.session?.import_session_Id || null,
        sessionCode: props.session?.session_code || null,
        sessionName: props.session?.session_name || "",
        fileName: props.session?.file_name || "",
        filePath: props.session?.file_path || "",
        fileSize: props.session?.file_size || 0,
        fileType: props.session?.file_type || "",
        totalRows: props.session?.total_rows || 0,
        
        // Step data
        headers: [],
        targetTable: props.session?.target_table || "",
        mappings: props.session?.mapping_config ? JSON.parse(props.session.mapping_config) : {},
        duplicateCheckFields: props.session?.duplicate_check_fields ? JSON.parse(props.session.duplicate_check_fields) : [],
        validationResults: [],
        
        // Processing data
        status: props.session?.status || "uploaded",
        processedRows: props.session?.processed_rows || 0,
        successCount: props.session?.success_count || 0,
        failedCount: props.session?.failed_count || 0,
        insertCount: props.session?.insert_count || 0,
        updateCount: props.session?.update_count || 0,
        skipCount: props.session?.skip_count || 0,
        startTime: props.session?.start_time || null,
        endTime: props.session?.end_time || null,
        resultFilePath: props.session?.result_file_path || null,
      },
      
      loading: false,
      error: null,
    };
    
    this.steps = [
      {
        id: 0,
        title: "Upload File",
        description: "Choose Excel or CSV file",
        component: FileUploadStep,
        canProceed: () => this.state.importSession.sessionId,
      },
      {
        id: 1,
        title: "Select Table",
        description: "Choose target database table",
        component: TableSelectionStep,
        canProceed: () => this.state.importSession.targetTable,
      },
      {
        id: 2,
        title: "Map Fields",
        description: "Map Excel columns to database fields",
        component: FieldMappingStep,
        canProceed: () => Object.keys(this.state.importSession.mappings).length > 0,
      },
      {
        id: 3,
        title: "Validate Data",
        description: "Preview and validate data",
        component: ValidationStep,
        canProceed: () => this.state.importSession.validationResults.length > 0,
      },
      {
        id: 4,
        title: "Processing",
        description: "Import data to database",
        component: ProcessingStep,
        canProceed: () => this.state.importSession.status === "completed" || this.state.importSession.status === "failed",
      },
      {
        id: 5,
        title: "Results",
        description: "View results and download report",
        component: ResultsStep,
        canProceed: () => false, // Final step
      },
    ];
  }
  
  getInitialStep = (session) => {
    switch (session.status) {
      case "uploaded":
        return 1; // Go to table selection
      case "mapping":
        return 2; // Go to field mapping
      case "validating":
        return 3; // Go to validation
      case "processing":
        return 4; // Go to processing
      case "completed":
      case "failed":
      case "cancelled":
        return 5; // Go to results
      default:
        return 0; // Start from upload
    }
  };

  componentDidMount() {
    // If viewing existing session, fetch latest data
    if (this.props.session) {
      this.fetchSessionData();
    }
  }

  fetchSessionData = () => {
    if (!this.state.importSession.sessionId) return;
    
    this.setState({ loading: true });
    
    fetch(`${configData.api_url}C_BulkImport/status?session_id=${this.state.importSession.sessionId}`)
      .then(response => response.json())
      .then(json => {
        if (json.status === "success") {
          this.setState({
            importSession: {
              ...this.state.importSession,
              status: json.import_status,
              processedRows: json.progress?.processed_rows || 0,
              successCount: json.progress?.success_count || 0,
              failedCount: json.progress?.failed_count || 0,
              insertCount: json.summary?.insert_count || 0,
              updateCount: json.summary?.update_count || 0,
              skipCount: json.summary?.skip_count || 0,
            },
            loading: false,
          });
        } else {
          this.showToast(json.message || "Failed to fetch session data", "error");
          this.setState({ loading: false });
        }
      })
      .catch(error => {
        console.error("Error:", error);
        this.showToast("Network error occurred", "error");
        this.setState({ loading: false });
      });
  };

  showToast = (message, type = "success") => {
    if (type === "success") {
      toast.success(message);
    } else {
      toast.error(message);
    }
  };

  updateSession = (updates) => {
    console.log("BulkImportWizard - Updating session with:", updates);
    
    // Update frontend state
    this.setState({
      importSession: {
        ...this.state.importSession,
        ...updates,
      },
    }, () => {
      console.log("BulkImportWizard - Updated session:", this.state.importSession);
      
      // If targetTable is being updated, also update it in the database immediately
      if (updates.targetTable && this.state.importSession.sessionId) {
        this.updateImportSessionInDatabase({
          target_table: updates.targetTable,
          status: updates.status || 1 // 1 = mapping
        });
      }
    });
  };
  
  updateImportSessionInDatabase = (data) => {
    const formData = new FormData();
    formData.append("session_id", this.state.importSession.sessionId);
    formData.append("sId", sessionStorage.getItem("sessionId"));
    formData.append("uId", sessionStorage.getItem("uId"));
    
    // Add the data to update
    Object.keys(data).forEach(key => {
      formData.append(key, data[key]);
    });
    
    console.log("BulkImportWizard - Updating import session in database:", data);
    
    fetch(configData.api_url + "C_BulkImport/update_session", {
      method: "POST", 
      body: formData,
    })
    .then(response => response.json())
    .then(json => {
      if (json.status === "success") {
        console.log("BulkImportWizard - Import session updated in database");
      } else {
        console.error("BulkImportWizard - Failed to update import session:", json.message);
      }
    })
    .catch(error => {
      console.error("BulkImportWizard - Error updating import session:", error);
    });
  };

  nextStep = () => {
    const { currentStep } = this.state;
    const step = this.steps[currentStep];
    const canProceed = step.canProceed();
    
    console.log(`BulkImportWizard - Step ${currentStep} (${step.title}) canProceed:`, canProceed);
    console.log("BulkImportWizard - Current session state:", this.state.importSession);
    
    if (canProceed) {
      this.setState({ currentStep: Math.min(currentStep + 1, this.steps.length - 1) });
    } else {
      this.showToast("Please complete the current step before proceeding", "error");
    }
  };

  prevStep = () => {
    this.setState({ currentStep: Math.max(this.state.currentStep - 1, 0) });
  };

  goToStep = (stepIndex) => {
    // Allow going to any previous step or current step
    if (stepIndex <= this.state.currentStep) {
      this.setState({ currentStep: stepIndex });
    }
  };

  getStepStatus = (stepIndex) => {
    const { currentStep } = this.state;
    if (stepIndex < currentStep) return "completed";
    if (stepIndex === currentStep) return "active";
    return "pending";
  };

  canGoNext = () => {
    const { currentStep } = this.state;
    const step = this.steps[currentStep];
    return step.canProceed() && currentStep < this.steps.length - 1;
  };

  canGoPrev = () => {
    return this.state.currentStep > 0;
  };

  render() {
    const { currentStep, importSession, loading } = this.state;
    const CurrentStepComponent = this.steps[currentStep].component;

    return (
      <div className="bulk-import-wizard">
        {/* Progress Steps Header */}
        <div className="import-steps">
          <Container fluid>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3 className="mb-0">
                <i className="fas fa-upload me-2"></i>
                Bulk Import Wizard
              </h3>
              <Button
                variant="outline-light"
                onClick={this.props.onClose}
                size="sm"
              >
                <i className="fas fa-times me-2"></i>
                Close
              </Button>
            </div>
            
            <div className="steps-container">
              {this.steps.map((step, index) => (
                <React.Fragment key={step.id}>
                  <div
                    className={`step ${this.getStepStatus(index)}`}
                    onClick={() => this.goToStep(index)}
                    style={{ cursor: index <= currentStep ? "pointer" : "default" }}
                  >
                    <div className="step-circle">
                      {this.getStepStatus(index) === "completed" ? (
                        <i className="fas fa-check"></i>
                      ) : (
                        step.id + 1
                      )}
                    </div>
                    <div className="step-content">
                      <div className="step-title">{step.title}</div>
                      <div className="step-description">{step.description}</div>
                    </div>
                  </div>
                  {index < this.steps.length - 1 && (
                    <div className={`step-connector ${this.getStepStatus(index) === "completed" ? "completed" : ""}`}></div>
                  )}
                </React.Fragment>
              ))}
            </div>
            
            {importSession.sessionCode && (
              <div className="text-center mt-3">
                <span className="badge bg-light text-dark px-3 py-2">
                  Session: {importSession.sessionCode}
                </span>
              </div>
            )}
          </Container>
        </div>

        {/* Step Content */}
        <div className="steps-content">
          {loading ? (
            <div className="text-center py-5">
              <div className="loading-spinner"></div>
              <div className="loading-text">Loading...</div>
            </div>
          ) : (
            <div className="step-panel">
              <CurrentStepComponent
                session={importSession}
                onUpdate={this.updateSession}
                onNext={this.nextStep}
                onError={(message) => this.showToast(message, "error")}
                onSuccess={(message) => this.showToast(message, "success")}
                sId={this.props.sId}
                uId={this.props.uId}
              />
            </div>
          )}
        </div>

        {/* Navigation Actions */}
        <div className="steps-actions">
          <div>
            {this.canGoPrev() && (
              <Button
                variant="outline-secondary"
                onClick={this.prevStep}
                className="btn-outline-custom"
              >
                <i className="fas fa-chevron-left me-2"></i>
                Previous
              </Button>
            )}
          </div>
          
          <div className="d-flex align-items-center gap-3">
            {currentStep < this.steps.length - 1 && (
              <>
                <small className="text-muted">
                  Step {currentStep + 1} of {this.steps.length}
                </small>
                {this.canGoNext() && (
                  <Button
                    variant="primary"
                    onClick={this.nextStep}
                    className="btn-primary-custom"
                  >
                    Next
                    <i className="fas fa-chevron-right ms-2"></i>
                  </Button>
                )}
              </>
            )}
            
            {currentStep === this.steps.length - 1 && (
              <Button
                variant="success"
                onClick={this.props.onClose}
                className="btn-primary-custom"
              >
                <i className="fas fa-check me-2"></i>
                Finish
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }
}